import { useAtom, useAtomValue } from 'jotai';
import { ToggleItemVisibility } from '../ToggleItemVisibility';
import { selectedPointOfInterest as selectedPointOfInterestAtom } from '@/components/Analysis/modules/pointOfInterest/state';
import { useEffect, useMemo } from 'react';
import {
  selectedSpherical as selectedSphericalAtom,
  voiVisibilityBox as voiVisibilityBoxAtom,
} from '@/components/Analysis/state';
import { convertCuboidToAdjustedVisibilityBoxProps } from '@/utils/geometry';
import { CuboidBase } from '@/modules/cuboids/types';

export const IsolateVoiPointCloud = () => {
  const selectedPointOfInterest = useAtomValue(selectedPointOfInterestAtom);
  const selectedSpherical = useAtomValue(selectedSphericalAtom);
  const [voiVisibilityBox, setVoiVisibilityBox] = useAtom(voiVisibilityBoxAtom);

  const cuboids = useMemo(
    () => selectedPointOfInterest?.cuboids,
    [selectedPointOfInterest?.cuboids]
  );

  const checked = !!voiVisibilityBox;

  const disabled = useMemo(() => {
    // If user's not in spherical view
    if (!selectedSpherical) return true;
    // If no POI is selected, toggle should be disabled
    if (!selectedPointOfInterest) return true;
    // If the selected POI is not of the type 'VOI' or 'Paint Block VOI' (workscope)
    if (!cuboids) return true;
    return false;
  }, [cuboids, selectedPointOfInterest, selectedSpherical]);

  // EDGE CASE:
  // When user is in spherical view and has the toggle turned on,
  // then they unselect the POI and then get out of the shperical
  // now the toggle is disabled and the visibility is set to the cuboid's view box
  // so we reset the visibility box to default whenever the selectedPointOfInterest is undefined
  useEffect(() => {
    if (!selectedPointOfInterest) {
      setVoiVisibilityBox(undefined);
    }
  }, [selectedPointOfInterest, setVoiVisibilityBox]);

  // EDGE CASE:
  // When user has the toggle on, and is in spherical view
  // then they get out of spherical view
  // now the toggle is disabled and the visibility is set to the cuboid's view box
  // so we reset the visibility box to default whenever the selectedPointOfInterest is undefined
  useEffect(() => {
    if (!selectedSpherical) {
      setVoiVisibilityBox(undefined);
    }
  }, [selectedSpherical, setVoiVisibilityBox]);

  const onToggle = () => {
    if (!cuboids?.[0]?.id) return;
    if (!checked) {
      const baseCuboid = cuboids?.[0] as CuboidBase;
      const target = convertCuboidToAdjustedVisibilityBoxProps(baseCuboid);
      setVoiVisibilityBox(target);
    } else {
      setVoiVisibilityBox(undefined);
    }
  };

  return (
    <>
      <ToggleItemVisibility
        label="Isolate VOI Point Cloud"
        disabled={disabled}
        isVisible={checked}
        toggleVisibility={onToggle}
      />
    </>
  );
};
